import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";

import {
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import { convertToCompanyTimezoneObject } from "../../../../../lib/functions";
import "../../../crmSharedStyle.css";

import {
    getCrmApiKeysApi,
  getCrmApiRequestLogsApi,
} from "../../../../../apiservice/crm";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const methodColors = {
  GET: "blue",
  POST: "green",
  PATCH: "orange",
  PUT: "gold",
  DELETE: "red",
};

const getStatusColor = (status) => {
  const value = Number(status);

  if (value >= 200 && value < 300) {
    return "green";
  }

  if (value === 401 || value === 403) {
    return "red";
  }

  if (value >= 400 && value < 500) {
    return "orange";
  }

  if (value >= 500) {
    return "red";
  }

  return "default";
};

const parseJson = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const JsonViewer = ({ value }) => {
  const parsed = parseJson(value);

  if (
    parsed === null ||
    parsed === undefined ||
    parsed === ""
  ) {
    return (
      <Text type="secondary">
        No data
      </Text>
    );
  }

  const display =
    typeof parsed === "string"
      ? parsed
      : JSON.stringify(parsed, null, 2);

  return (
    <pre className="api-code-block api-request-json">
      <code>{display}</code>
    </pre>
  );
};

const ApiRequestHistory = () => {
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [selectedLog, setSelectedLog] =  useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);  
  const [pagination, setPagination] =
    useState({
      current: 1,
      pageSize: 20,
      total: 0,
    });
  const [apiKeys, setApiKeys] =  useState([]);
  const [filters, setFilters] = useState({
    method: null,
    response_status: null,
    api_key_id: null,
    date_from: null,
    date_to: null,
    });

    const [appliedFilters, setAppliedFilters] = useState({
    method: null,
    response_status: null,
    api_key_id: null,
    date_from: null,
    date_to: null,
    });
  
  const loadLogs = useCallback(
    async (
        page = 1,
        pageSize = 20,
        activeFilters = appliedFilters
    ) => {
        try {
        setLoading(true);

        const res = await getCrmApiRequestLogsApi({
            page,
            limit: pageSize,

            method:
            activeFilters.method || undefined,

            response_status:
            activeFilters.response_status ?? undefined,

            api_key_id:
            activeFilters.api_key_id || undefined,

            date_from:
            activeFilters.date_from || undefined,

            date_to:
            activeFilters.date_to || undefined,
        });

        const responseData = res?.data || {};
        const paginationData =
            responseData.pagination || {};

        setRows(responseData.data || []);

        setPagination({
            current:
            paginationData.page || page,

            pageSize:
            paginationData.limit || pageSize,

            total:
            paginationData.total || 0,
        });
        } catch (err) {
        console.error(err);

        message.error(
            err?.response?.data?.message ||
            "Failed to load API request history"
        );
        } finally {
        setLoading(false);
        }
    },
    [appliedFilters]
    );

  useEffect(() => {
    loadLogs(1, pagination.pageSize);
  }, [filters]);

  useEffect(() => {
    const loadApiKeys = async () => {
        try {
        const res =
            await getCrmApiKeysApi();
        setApiKeys(res?.data?.data || []);
        } catch (err) {
        console.error(
            "Failed to load API keys",
            err
        );
        }
    };

    loadApiKeys();
    }, []);

  const handleDateChange = (dates) => {
    setFilters((prev) => ({
      ...prev,

      date_from: dates?.[0]
        ? dates[0]
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss")
        : null,

      date_to: dates?.[1]
        ? dates[1]
            .endOf("day")
            .format("YYYY-MM-DD HH:mm:ss")
        : null,
    }));
  };

  const handleTableChange = (nextPagination) => {
    loadLogs(
      nextPagination.current,
      nextPagination.pageSize
    );
  };

  const handleView = (record) => {
    setSelectedLog(record);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    };

    const resetFilters = () => {
    const emptyFilters = {
        method: null,
        response_status: null,
        api_key_id: null,
        date_from: null,
        date_to: null,
    };

    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    };

  const columns = [
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 100,

      render: (method) => (
        <Tag
          color={
            methodColors[method] ||
            "default"
          }
          className="api-request-method"
        >
          {method}
        </Tag>
      ),
    },

    {
      title: "Endpoint",
      dataIndex: "endpoint",
      key: "endpoint",

      ellipsis: true,

      render: (value) => (
        <Tooltip title={value}>
          <span className="api-request-endpoint">
            {value}
          </span>
        </Tooltip>
      ),
    },

    {
        title: "API Key",
        key: "api_key",

        width: 180,

        render: (_, record) => (
            <div>
            <div style={{ fontWeight: 500 }}>
                {record.api_key_name ||
                "API Integration"}
            </div>

            <Text
                type="secondary"
                style={{ fontSize: 12 }}
            >
                {record.key_prefix || "-"}
            </Text>
            </div>
        ),
        },

    {
      title: "Status",
      dataIndex: "response_status",
      key: "response_status",
      width: 100,

      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
    },

    {
      title: "Duration",
      dataIndex: "duration_ms",
      key: "duration_ms",
      width: 110,

      render: (value) =>
        value !== null &&
        value !== undefined
          ? `${value} ms`
          : "-",
    },

    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 150,

      render: (value) => value || "-",
    },

    {
      title: "Requested At",
      dataIndex: "created_at",
      key: "created_at",
      width: 190,

      render: (value) =>
        value
          ? convertToCompanyTimezoneObject(value).format(
              "DD MMM YYYY, hh:mm A"
            )
          : "-",
    },

    {
      title: "",
      key: "actions",
      width: 70,
      align: "center",

      render: (_, record) => (
        <Tooltip title="View request">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() =>
              handleView(record)
            }
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="api-doc-section">
      <div className="api-request-history-toolbar">
        <div>
          <Title
            level={4}
            style={{ marginBottom: 2 }}
          >
            Request History
          </Title>

          <Text type="secondary">
            Monitor requests made through your
            Public CRM API.
          </Text>
        </div>

        <Button
          icon={<ReloadOutlined />}
          onClick={() =>
            loadLogs(
              pagination.current,
              pagination.pageSize
            )
          }
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div className="api-request-history-filters">
          <Select
            placeholder="Method"
            allowClear
            style={{ width: 130 }}
            value={
              filters.method ?? undefined
            }
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                method: value,
              }))
            }
            options={[
              {
                label: "GET",
                value: "GET",
              },
              {
                label: "POST",
                value: "POST",
              },
              {
                label: "PATCH",
                value: "PATCH",
              },
              {
                label: "PUT",
                value: "PUT",
              },
              {
                label: "DELETE",
                value: "DELETE",
              },
            ]}
          />

          <Select
            placeholder="Response Status"
            allowClear
            style={{ width: 170 }}
            value={
              filters.response_status ??
              undefined
            }
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                response_status: value,
              }))
            }
            options={[
              {
                label: "200 OK",
                value: 200,
              },
              {
                label: "201 Created",
                value: 201,
              },
              {
                label: "400 Bad Request",
                value: 400,
              },
              {
                label: "401 Unauthorized",
                value: 401,
              },
              {
                label: "403 Forbidden",
                value: 403,
              },
              {
                label: "404 Not Found",
                value: 404,
              },
              {
                label:
                  "409 Conflict",
                value: 409,
              },
              {
                label:
                  "422 Unprocessable",
                value: 422,
              },
              {
                label:
                  "429 Too Many Requests",
                value: 429,
              },
              {
                label:
                  "500 Server Error",
                value: 500,
              },
            ]}
          />

          <Select
            placeholder="API Key"
            allowClear
            style={{ width: 200 }}

            value={
                filters.api_key_id ??
                undefined
            }

            onChange={(value) =>
                setFilters((prev) => ({
                ...prev,
                api_key_id: value,
                }))
            }

            options={apiKeys.map((key) => ({
                label:
                key.name ||
                key.key_prefix ||
                "API Key",

                value: key.id,
            }))}
            />

          <RangePicker
            value={
                filters.date_from && filters.date_to
                ? [
                    dayjs(filters.date_from),
                    dayjs(filters.date_to),
                    ]
                : null
            }
            onChange={handleDateChange}
            />

          <Button
            type="primary"
            onClick={applyFilters}
            >
            Apply Filters
            </Button>

            <Button onClick={resetFilters}>
            Reset
            </Button> 
        </div>
      </Card>

      <Card
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [
              10,
              20,
              50,
              100,
            ],
            showTotal: (total) =>
              `${total} requests`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
                description="No API requests found"
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title="API Request Details"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedLog(null);
        }}
        className="crm-drawer"
        width={
          window.innerWidth < 768
            ? "100%"
            : 700
        }
        styles={{
          wrapper: {
            paddingTop: window.innerWidth < 768 ? "80px" : undefined
          }
        }}

      >
        {selectedLog && (
          <>
            <div className="api-request-detail-grid">
                <div className="api-request-detail-item">
                    <span className="api-request-detail-label">
                        API Integration
                    </span>

                    <span className="api-request-detail-value">
                        {selectedLog.api_key_name ||
                        "API Integration"}
                    </span>
                    </div>

                    <div className="api-request-detail-item">
                    <span className="api-request-detail-label">
                        API Key Prefix
                    </span>

                    <span className="api-request-detail-value">
                        {selectedLog.key_prefix || "-"}
                    </span>
                    </div>
              <div className="api-request-detail-item">
                <span className="api-request-detail-label">
                  Method
                </span>

                <Tag
                  color={
                    methodColors[
                      selectedLog.method
                    ] || "default"
                  }
                >
                  {selectedLog.method}
                </Tag>
              </div>

              <div className="api-request-detail-item">
                <span className="api-request-detail-label">
                  Response Status
                </span>

                <Tag
                  color={getStatusColor(
                    selectedLog.response_status
                  )}
                >
                  {
                    selectedLog.response_status
                  }
                </Tag>
              </div>

              <div className="api-request-detail-item">
                <span className="api-request-detail-label">
                  Duration
                </span>

                <span className="api-request-detail-value">
                  {selectedLog.duration_ms ??
                    "-"}{" "}
                  ms
                </span>
              </div>

              <div className="api-request-detail-item">
                <span className="api-request-detail-label">
                  IP Address
                </span>

                <span className="api-request-detail-value">
                  {selectedLog.ip_address ||
                    "-"}
                </span>
              </div>
            </div>

            <Title level={5}>
              Endpoint
            </Title>

            <pre className="api-code-block">
              <code>
                {selectedLog.method}{" "}
                {selectedLog.endpoint}
              </code>
            </pre>

            <Title level={5}>
              Request Body
            </Title>

            <JsonViewer
              value={
                selectedLog.request_body
              }
            />

            <Title level={5}>
              Response Body
            </Title>

            <JsonViewer
              value={
                selectedLog.response_body
              }
            />

            <Title level={5}>
              User Agent
            </Title>

            <Text>
              {selectedLog.user_agent ||
                "-"}
            </Text>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ApiRequestHistory;